import * as XLSX from 'xlsx';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import { DOCUMENT_STATUS, REPORT_TYPE } from '@/types/document';
import { useStorage } from '@/composables/useStorage';
import { StorageBucket } from '@/types/storage';
/**
 * Service for merging Excel files from different report types
 */
export class ExcelMergeService {
    constructor() {
        Object.defineProperty(this, "TEMPLATE_SHEET_NAME", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'template_revexp'
        });
        Object.defineProperty(this, "HEADER_ROWS", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 4
        }); // A1:AW4
    }
    /**
     * Merges Excel files from documents in REVISION_NEEDED status
     * @returns Promise resolving to the merged Excel file as ArrayBuffer
     */
    async mergeRevisionDocuments() {
        try {
            logger.info('Starting to merge revision documents', LogCategory.GENERAL);
            console.log("Starting mergeRevisionDocuments");
            // Get all documents in REVISION_NEEDED status
            const documents = await this.getRevisionDocuments();
            console.log("Retrieved documents:", documents);
            if (!documents || documents.length === 0) {
                console.error("No documents in REVISION_NEEDED status found");
                logger.error('No documents in REVISION_NEEDED status found', LogCategory.GENERAL);
                throw new Error('No documents in REVISION_NEEDED status found');
            }
            logger.info('Found documents to merge', LogCategory.GENERAL, {
                documentCount: documents.length,
                documentIds: documents.map(doc => doc.id),
                reportTypes: documents.map(doc => doc.report_type)
            });
            console.log("Documents to merge:", {
                documentCount: documents.length,
                documentIds: documents.map(doc => doc.id),
                reportTypes: documents.map(doc => doc.report_type)
            });
            // Download and process each document
            const workbooks = [];
            const processedDocuments = [];
            const failedDocuments = [];
            for (const doc of documents) {
                try {
                    const fileId = doc.file_id;
                    if (!fileId) {
                        logger.warn('Document has no file_id', LogCategory.GENERAL, { documentId: doc.id });
                        failedDocuments.push({ id: doc.id, reason: 'No file_id' });
                        continue;
                    }
                    logger.info('Processing document', LogCategory.GENERAL, {
                        documentId: doc.id,
                        fileId,
                        reportType: doc.report_type
                    });
                    // Get file URL
                    const fileUrl = await this.getFileUrl(fileId);
                    if (!fileUrl) {
                        logger.warn('Could not get URL for file', LogCategory.GENERAL, {
                            documentId: doc.id,
                            fileId
                        });
                        failedDocuments.push({ id: doc.id, reason: 'Could not get file URL' });
                        continue;
                    }
                    // Download file
                    const fileData = await this.downloadFile(fileUrl);
                    if (!fileData) {
                        logger.warn('Could not download file', LogCategory.GENERAL, {
                            documentId: doc.id,
                            fileId,
                            fileUrl
                        });
                        failedDocuments.push({ id: doc.id, reason: 'Could not download file' });
                        continue;
                    }
                    logger.info('Successfully downloaded file', LogCategory.GENERAL, {
                        documentId: doc.id,
                        fileSize: fileData.byteLength
                    });
                    // Parse Excel file
                    try {
                        const workbook = XLSX.read(fileData, { type: 'array' });
                        // Check if workbook has sheets
                        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                            logger.warn('Workbook has no sheets', LogCategory.GENERAL, { documentId: doc.id });
                            failedDocuments.push({ id: doc.id, reason: 'Workbook has no sheets' });
                            continue;
                        }
                        workbooks.push(workbook);
                        processedDocuments.push(doc.id);
                        logger.info('Successfully processed document', LogCategory.GENERAL, {
                            documentId: doc.id,
                            sheetNames: workbook.SheetNames
                        });
                    }
                    catch (parseError) {
                        logger.error('Error parsing Excel file', LogCategory.GENERAL, {
                            documentId: doc.id,
                            error: parseError
                        });
                        failedDocuments.push({ id: doc.id, reason: 'Error parsing Excel file' });
                        continue;
                    }
                }
                catch (error) {
                    logger.error('Error processing document', LogCategory.GENERAL, {
                        documentId: doc.id,
                        error
                    });
                    failedDocuments.push({ id: doc.id, reason: 'Processing error' });
                }
            }
            logger.info('Document processing summary', LogCategory.GENERAL, {
                totalDocuments: documents.length,
                processedDocuments: processedDocuments.length,
                failedDocuments: failedDocuments.length,
                processedIds: processedDocuments,
                failedDetails: failedDocuments
            });
            if (workbooks.length === 0) {
                logger.error('No workbooks could be processed', LogCategory.GENERAL, {
                    failedDocuments
                });
                throw new Error('No workbooks could be processed');
            }
            // Merge workbooks
            logger.info('Merging workbooks', LogCategory.GENERAL, {
                workbookCount: workbooks.length
            });
            const mergedWorkbook = await this.mergeWorkbooks(workbooks);
            // Convert to ArrayBuffer
            logger.info('Converting merged workbook to ArrayBuffer', LogCategory.GENERAL);
            const mergedBuffer = XLSX.write(mergedWorkbook, { bookType: 'xlsx', type: 'array' });
            logger.info('Successfully merged all workbooks', LogCategory.GENERAL, {
                bufferSize: mergedBuffer.byteLength,
                processedDocuments: processedDocuments.length
            });
            return mergedBuffer;
        }
        catch (error) {
            logger.error('Error merging revision documents', LogCategory.GENERAL, { error });
            throw error;
        }
    }
    /**
     * Gets all documents in REVISION_NEEDED status
     * @returns Promise resolving to array of documents
     */
    async getRevisionDocuments() {
        try {
            logger.info('Fetching documents in REVISION_NEEDED status', LogCategory.DATABASE);
            console.log("Starting to fetch revision documents");
            console.log("DOCUMENT_STATUS.REVISION_NEEDED =", DOCUMENT_STATUS.REVISION_NEEDED);
            // Verwende die documents Tabelle statt document_profile
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { data, error } = await supabase
                .from('documents')
                .select('*')
                .eq('status', DOCUMENT_STATUS.REVISION_NEEDED);
            console.log("Supabase query result:", { data, error });
            if (error) {
                console.error("Error in Supabase query:", error);
                logger.error('Error in Supabase query for revision documents', LogCategory.DATABASE, { error });
                throw error;
            }
            if (!data || data.length === 0) {
                console.warn("No documents found with status REVISION_NEEDED");
                logger.warn('No documents found in REVISION_NEEDED status', LogCategory.DATABASE);
                return [];
            }
            // Filter out documents without file_id and exclude CAL report type
            const validDocuments = data.filter(doc => doc.file_id &&
                doc.report_type !== REPORT_TYPE.CAL);
            console.log("Valid documents with file_id (excluding CAL):", validDocuments);
            if (validDocuments.length === 0) {
                console.warn("No valid documents found (with file_id and not CAL)");
                logger.warn('No valid documents found (with file_id and not CAL)', LogCategory.DATABASE);
            }
            else {
                console.log("Found documents in REVISION_NEEDED status:", {
                    totalCount: data.length,
                    validCount: validDocuments.length,
                    reportTypes: validDocuments.map(doc => doc.report_type)
                });
                logger.info('Found documents in REVISION_NEEDED status', LogCategory.DATABASE, {
                    totalCount: data.length,
                    validCount: validDocuments.length,
                    reportTypes: validDocuments.map(doc => doc.report_type)
                });
            }
            return validDocuments;
        }
        catch (error) {
            console.error("Exception in getRevisionDocuments:", error);
            logger.error('Error fetching revision documents', LogCategory.DATABASE, { error });
            throw error;
        }
    }
    /**
     * Gets the URL for a file
     * @param fileId The file ID
     * @returns Promise resolving to the file URL
     */
    async getFileUrl(fileId) {
        try {
            logger.info('Fetching URL for file', LogCategory.DATABASE, { fileId });
            const { data, error } = await supabase
                .from('files')
                .select('url, name, type')
                .eq('id', fileId)
                .single();
            if (error) {
                logger.error('Error in Supabase query for file URL', LogCategory.DATABASE, { error, fileId });
                throw error;
            }
            if (!data || !data.url) {
                logger.warn('No URL found for file', LogCategory.DATABASE, { fileId });
                return null;
            }
            logger.info('Found URL for file', LogCategory.DATABASE, {
                fileId,
                fileName: data.name,
                fileType: data.type,
                url: data.url
            });
            return data.url;
        }
        catch (error) {
            logger.error('Error fetching file URL', LogCategory.DATABASE, { error, fileId });
            return null;
        }
    }
    /**
     * Downloads a file from a URL
     * @param url The file URL
     * @returns Promise resolving to the file data as ArrayBuffer
     */
    async downloadFile(url) {
        try {
            try {
                const response = await fetch(url);
                if (response.ok) {
                    logger.info('Successfully downloaded file directly from URL', LogCategory.STORAGE);
                    return await response.arrayBuffer();
                }
                logger.warn('Failed to download file directly from URL, trying Supabase storage', LogCategory.STORAGE);
            }
            catch (fetchError) {
                logger.warn('Error fetching file directly from URL', LogCategory.STORAGE, { fetchError });
            }
            // If direct download fails, try Supabase storage
            // Extract the file path from the URL
            const filePath = this.extractFilePathFromUrl(url);
            if (!filePath) {
                throw new Error(`Could not extract file path from URL: ${url}`);
            }
            logger.info('Extracted file path from URL', LogCategory.STORAGE, { filePath });
            // Get the file from Supabase storage
            const { data, error: storageError } = await supabase.storage
                .from('templates')
                .download(filePath);
            if (storageError) {
                logger.error('Error downloading file from Supabase storage', LogCategory.STORAGE, { error: storageError, filePath });
                throw storageError;
            }
            logger.info('Successfully downloaded file from Supabase storage', LogCategory.STORAGE);
            return await data.arrayBuffer();
        }
        catch (error) {
            logger.error('Error downloading file', LogCategory.STORAGE, { error, url });
            return null;
        }
    }
    /**
     * Extracts the file path from a URL
     * @param url The URL
     * @returns The file path
     */
    extractFilePathFromUrl(url) {
        if (!url)
            return null;
        try {
            // Try to parse the URL
            const parsedUrl = new URL(url);
            // Get the pathname (e.g., /templates/file.xlsx)
            const pathname = parsedUrl.pathname;
            // Extract the part after the last '/' character
            const parts = pathname.split('/');
            const filename = parts[parts.length - 1];
            logger.info('Extracted filename from URL', LogCategory.STORAGE, { url, filename });
            return filename;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        }
        catch (error) {
            // If URL parsing fails, fall back to simple string splitting
            logger.warn('Failed to parse URL, falling back to simple extraction', LogCategory.STORAGE, { url });
            // Extract the part after the last '/' character
            const parts = url.split('/');
            return parts[parts.length - 1];
        }
    }
    /**
     * Checks if a row is a data row by verifying if the first column (Account number) contains a numeric value
     * @param row The row to check
     * @returns True if the row is a data row, false otherwise
     */
    isDataRow(row) {
        // Check if the row exists and has at least one element
        if (!row || !Array.isArray(row) || row.length === 0) {
            return false;
        }
        const accountNumber = row[0];
        // Check if the account number is defined and not empty
        if (accountNumber === undefined || accountNumber === null || accountNumber === '') {
            return false;
        }
        // Check if the account number is a number or a string containing only digits
        if (typeof accountNumber === 'number' ||
            (typeof accountNumber === 'string' && /^\d+$/.test(accountNumber))) {
            return true;
        }
        return false;
    }
    /**
     * Merges multiple workbooks into one
     * @param workbooks Array of workbooks to merge
     * @returns Promise resolving to the merged workbook
     */
    async mergeWorkbooks(workbooks) {
        // Create a new workbook
        const mergedWorkbook = XLSX.utils.book_new();
        logger.info('Starting to merge workbooks', LogCategory.GENERAL, {
            workbookCount: workbooks.length
        });
        // Find the template_revexp sheet in each workbook
        const sheetsData = [];
        const processedWorkbooks = [];
        for (let i = 0; i < workbooks.length; i++) {
            try {
                const workbook = workbooks[i];
                // Check if workbook has sheets
                if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                    logger.warn('Workbook has no sheets', LogCategory.GENERAL, { workbookIndex: i });
                    continue;
                }
                const sheetName = this.findSheetByName(workbook, this.TEMPLATE_SHEET_NAME);
                if (!sheetName) {
                    logger.warn(`Sheet ${this.TEMPLATE_SHEET_NAME} not found in workbook`, LogCategory.GENERAL, {
                        workbookIndex: i,
                        availableSheets: workbook.SheetNames
                    });
                    continue;
                }
                const sheet = workbook.Sheets[sheetName];
                if (!sheet) {
                    logger.warn('Sheet is undefined or null', LogCategory.GENERAL, {
                        workbookIndex: i,
                        sheetName
                    });
                    continue;
                }
                // Check if sheet has a valid range
                if (!sheet['!ref']) {
                    logger.warn('Sheet has no range (!ref)', LogCategory.GENERAL, {
                        workbookIndex: i,
                        sheetName
                    });
                    continue;
                }
                // Convert sheet to JSON
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                if (!jsonData || jsonData.length === 0) {
                    logger.warn('Sheet converted to empty JSON data', LogCategory.GENERAL, {
                        workbookIndex: i,
                        sheetName
                    });
                    continue;
                }
                if (jsonData.length <= this.HEADER_ROWS) {
                    logger.warn('Sheet has only header rows, no data rows', LogCategory.GENERAL, {
                        workbookIndex: i,
                        sheetName,
                        rowCount: jsonData.length
                    });
                    continue;
                }
                // Add data rows (skip header rows)
                const allDataRows = jsonData.slice(this.HEADER_ROWS);
                // Filter out non-data rows (rows without numeric account numbers)
                const dataRows = allDataRows.filter(row => this.isDataRow(row));
                logger.info('Filtered data rows', LogCategory.GENERAL, {
                    workbookIndex: i,
                    totalRows: allDataRows.length,
                    dataRows: dataRows.length,
                    filteredOut: allDataRows.length - dataRows.length
                });
                if (dataRows.length === 0) {
                    logger.warn('No data rows found after filtering', LogCategory.GENERAL, {
                        workbookIndex: i,
                        sheetName
                    });
                    continue;
                }
                sheetsData.push(dataRows);
                processedWorkbooks.push(i);
                logger.info('Successfully processed workbook sheet', LogCategory.GENERAL, {
                    workbookIndex: i,
                    sheetName,
                    dataRowCount: dataRows.length
                });
            }
            catch (error) {
                logger.error('Error processing workbook', LogCategory.GENERAL, {
                    workbookIndex: i,
                    error
                });
            }
        }
        if (sheetsData.length === 0) {
            throw new Error(`No sheets with name ${this.TEMPLATE_SHEET_NAME} found or all sheets were empty`);
        }
        logger.info('Successfully processed workbooks', LogCategory.GENERAL, {
            totalWorkbooks: workbooks.length,
            processedWorkbooks: processedWorkbooks,
            sheetsWithData: sheetsData.length
        });
        // Find a workbook with the template sheet to get header rows
        let headerRows = [];
        let headerWorkbookIndex = -1;
        // First try to use the first processed workbook for headers
        if (processedWorkbooks.length > 0) {
            headerWorkbookIndex = processedWorkbooks[0];
        }
        else {
            // If no processed workbooks, try to find any workbook with the sheet
            for (let i = 0; i < workbooks.length; i++) {
                const workbook = workbooks[i];
                const sheetName = this.findSheetByName(workbook, this.TEMPLATE_SHEET_NAME);
                if (sheetName) {
                    headerWorkbookIndex = i;
                    break;
                }
            }
        }
        if (headerWorkbookIndex === -1) {
            // If still no workbook found, create default headers
            logger.warn('No workbook found with template sheet for headers, creating default headers', LogCategory.GENERAL);
            // Create empty header rows (4 rows)
            for (let i = 0; i < this.HEADER_ROWS; i++) {
                headerRows.push([]);
            }
        }
        else {
            // Get header rows from the selected workbook
            const headerWorkbook = workbooks[headerWorkbookIndex];
            const headerSheetName = this.findSheetByName(headerWorkbook, this.TEMPLATE_SHEET_NAME);
            if (headerSheetName) {
                const headerSheet = headerWorkbook.Sheets[headerSheetName];
                headerRows = XLSX.utils.sheet_to_json(headerSheet, { header: 1 }).slice(0, this.HEADER_ROWS);
                logger.info('Using header rows from workbook', LogCategory.GENERAL, {
                    workbookIndex: headerWorkbookIndex,
                    headerRowCount: headerRows.length
                });
            }
        }
        // Combine header rows with data rows
        const mergedData = [...headerRows];
        // Add all data rows (already filtered to include only rows with numeric account numbers)
        let totalDataRows = 0;
        for (const sheetData of sheetsData) {
            for (const row of sheetData) {
                if (row && Array.isArray(row)) {
                    mergedData.push(row);
                    totalDataRows++;
                }
            }
        }
        logger.info('Merged data rows', LogCategory.GENERAL, {
            headerRowCount: headerRows.length,
            dataRowCount: totalDataRows,
            totalRowCount: mergedData.length
        });
        // Create a new sheet from the merged data
        const mergedSheet = XLSX.utils.aoa_to_sheet(mergedData);
        // Add the merged sheet to the workbook
        XLSX.utils.book_append_sheet(mergedWorkbook, mergedSheet, this.TEMPLATE_SHEET_NAME);
        return mergedWorkbook;
    }
    /**
     * Finds a sheet by name in a workbook
     * @param workbook The workbook
     * @param name The sheet name to find
     * @returns The sheet name or undefined if not found
     */
    findSheetByName(workbook, name) {
        // Log all sheet names for debugging
        logger.info('Available sheets in workbook', LogCategory.GENERAL, {
            sheetNames: workbook.SheetNames
        });
        // First try exact match
        let sheetName = workbook.SheetNames.find((n) => n === name);
        // If no exact match, try case-insensitive match
        if (!sheetName) {
            sheetName = workbook.SheetNames.find((n) => n.toLowerCase() === name.toLowerCase());
        }
        // If still no match, try includes
        if (!sheetName) {
            sheetName = workbook.SheetNames.find((n) => n.toLowerCase().includes(name.toLowerCase()));
        }
        if (sheetName) {
            logger.info('Found sheet matching name', LogCategory.GENERAL, {
                searchName: name,
                foundSheet: sheetName
            });
        }
        else {
            logger.warn('No sheet found matching name', LogCategory.GENERAL, {
                searchName: name,
                availableSheets: workbook.SheetNames
            });
        }
        return sheetName;
    }
    /**
     * Creates a new document in the database for the merged file and uploads it to the Templates bucket
     * @param fileBuffer The merged file as ArrayBuffer
     * @param userId The ID of the user creating the document
     * @returns Promise resolving to the created document
     */
    async createMergedDocument(fileBuffer, userId) {
        try {
            logger.info('Creating merged document for storage', LogCategory.GENERAL, {
                bufferSize: fileBuffer.byteLength,
                userId
            });
            const timestamp = new Date().getTime();
            const fileName = `merged_template_revexp_${timestamp}.xlsx`;
            // Upload the file to the Templates bucket
            try {
                const { uploadFile } = useStorage();
                // Convert ArrayBuffer to File object
                const file = new File([fileBuffer], fileName, {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                logger.info('Uploading merged document to Templates bucket', LogCategory.STORAGE, {
                    fileName,
                    fileSize: file.size
                });
                // Upload to the Templates bucket
                const uploadResult = await uploadFile(StorageBucket.TEMPLATES, // bucket name
                fileName, // path
                file, // file
                {
                    onlyPcoAccess: true,
                    documentStatus: DOCUMENT_STATUS.READY_FOR_DOWNLOAD,
                    userId: userId,
                    reportType: REPORT_TYPE.PCO,
                    createDocumentEntry: true
                });
                logger.info('Merged document uploaded to Templates bucket successfully', LogCategory.STORAGE, {
                    fileName,
                    uploadResult
                });
                // Return document object with upload information
                return {
                    id: uploadResult.id || ('merged-' + timestamp),
                    name: fileName,
                    description: 'Merged template_revexp (uploaded to Templates bucket)',
                    status: DOCUMENT_STATUS.READY_FOR_DOWNLOAD,
                    created_by: userId,
                    report_type: REPORT_TYPE.PCO,
                    uploaded: true,
                    upload_info: uploadResult,
                    timestamp: timestamp
                };
            }
            catch (uploadError) {
                // Log the error but don't fail the entire operation
                logger.error('Error uploading merged document to Templates bucket', LogCategory.STORAGE, {
                    error: uploadError,
                    fileName
                });
                // Return a document object with error information
                return {
                    id: 'merged-' + timestamp,
                    name: fileName,
                    description: 'Merged template_revexp (upload failed)',
                    status: DOCUMENT_STATUS.READY_FOR_DOWNLOAD,
                    created_by: userId,
                    report_type: REPORT_TYPE.PCO,
                    uploaded: false,
                    upload_error: uploadError instanceof Error ? uploadError.message : String(uploadError),
                    timestamp: timestamp
                };
            }
        }
        catch (error) {
            logger.error('Error creating merged document', LogCategory.GENERAL, { error });
            throw error;
        }
    }
    /**
     * Downloads a merged document by its file path
     * @param filePath The path of the file in the Templates bucket
     * @returns Promise resolving to true if successful
     */
    async downloadMergedDocument(filePath) {
        try {
            // Validate and normalize the file path
            if (!filePath) {
                logger.error('Invalid file path: empty or undefined', LogCategory.STORAGE);
                return false;
            }
            // Remove any leading slashes or folder paths, we just want the filename
            const normalizedFilePath = filePath.split('/').pop() || filePath;
            logger.info('Downloading merged document', LogCategory.GENERAL, {
                originalFilePath: filePath,
                normalizedFilePath
            });
            // Use the useStorage composable which handles the correct URL generation
            const { downloadFileByName, getPublicUrl } = useStorage();
            try {
                // First try to download using the downloadFileByName method
                // This method retrieves the URL from the database and triggers the browser download
                logger.info('Trying to download file by name', LogCategory.STORAGE, {
                    fileName: normalizedFilePath
                });
                const publicUrl = await downloadFileByName(normalizedFilePath, StorageBucket.TEMPLATES);
                if (publicUrl) {
                    logger.info('Successfully downloaded file using downloadFileByName', LogCategory.STORAGE, {
                        fileName: normalizedFilePath,
                        publicUrl
                    });
                    return true;
                }
            }
            catch (downloadError) {
                logger.warn('Error downloading file by name, trying alternative method', LogCategory.STORAGE, {
                    error: downloadError,
                    fileName: normalizedFilePath
                });
                // Continue to try alternative method
            }
            // If downloadFileByName fails, try using the public URL directly
            try {
                // Get the public URL for the file (this includes the /public/ path)
                const publicUrl = getPublicUrl(StorageBucket.TEMPLATES, normalizedFilePath);
                logger.info('Generated public URL for file', LogCategory.STORAGE, {
                    fileName: normalizedFilePath,
                    publicUrl
                });
                // Check if the URL contains /public/ - if not, modify it
                let finalUrl = publicUrl;
                if (!finalUrl.includes('/public/')) {
                    // Insert /public/ into the URL path
                    const urlObj = new URL(finalUrl);
                    const pathParts = urlObj.pathname.split('/');
                    // Find the position after /object/ in the path
                    const objectIndex = pathParts.findIndex(part => part === 'object');
                    if (objectIndex !== -1 && objectIndex < pathParts.length - 1) {
                        // Insert 'public' after 'object'
                        pathParts.splice(objectIndex + 1, 0, 'public');
                        urlObj.pathname = pathParts.join('/');
                        finalUrl = urlObj.toString();
                        logger.info('Modified URL to include /public/', LogCategory.STORAGE, {
                            originalUrl: publicUrl,
                            modifiedUrl: finalUrl
                        });
                    }
                }
                // Try to download using the public URL
                const response = await fetch(finalUrl);
                if (response.ok) {
                    const blob = await response.blob();
                    // Create a URL for the Blob
                    const url = URL.createObjectURL(blob);
                    // Create a download link and trigger the download
                    const link = document.createElement('a');
                    const fileName = normalizedFilePath.split('/').pop() || 'merged_document.xlsx';
                    link.href = url;
                    link.download = fileName;
                    link.style.display = 'none';
                    // Add to the document and click
                    document.body.appendChild(link);
                    link.click();
                    // Clean up
                    setTimeout(() => {
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                    }, 100);
                    logger.info('Document download triggered via public URL', LogCategory.GENERAL, { fileName });
                    return true;
                }
                else {
                    logger.warn('Failed to download file using public URL', LogCategory.STORAGE, {
                        status: response.status,
                        statusText: response.statusText,
                        url: finalUrl
                    });
                    // Continue to try signed URL method
                }
            }
            catch (publicUrlError) {
                logger.warn('Error using public URL', LogCategory.STORAGE, {
                    error: publicUrlError,
                    fileName: normalizedFilePath
                });
                // Continue to try signed URL method
            }
            // Try using a signed URL as another fallback
            try {
                const { data: urlData } = await supabase.storage
                    .from(StorageBucket.TEMPLATES)
                    .createSignedUrl(normalizedFilePath, 60); // 60 seconds expiry
                if (urlData && urlData.signedUrl) {
                    logger.info('Successfully created signed URL for file', LogCategory.STORAGE, {
                        filePath: normalizedFilePath,
                        signedUrl: urlData.signedUrl
                    });
                    // Try to download using the signed URL
                    const response = await fetch(urlData.signedUrl);
                    if (response.ok) {
                        const blob = await response.blob();
                        // Create a URL for the Blob
                        const url = URL.createObjectURL(blob);
                        // Create a download link and trigger the download
                        const link = document.createElement('a');
                        const fileName = normalizedFilePath.split('/').pop() || 'merged_document.xlsx';
                        link.href = url;
                        link.download = fileName;
                        link.style.display = 'none';
                        // Add to the document and click
                        document.body.appendChild(link);
                        link.click();
                        // Clean up
                        setTimeout(() => {
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                        }, 100);
                        logger.info('Document download triggered via signed URL', LogCategory.GENERAL, { fileName });
                        return true;
                    }
                    else {
                        logger.warn('Failed to download file using signed URL', LogCategory.STORAGE, {
                            status: response.status,
                            statusText: response.statusText,
                            signedUrl: urlData.signedUrl
                        });
                    }
                }
            }
            catch (signedUrlError) {
                logger.warn('Error creating signed URL', LogCategory.STORAGE, {
                    error: signedUrlError,
                    filePath: normalizedFilePath
                });
            }
            // If all methods above fail, try direct download with /public/ in the path
            try {
                logger.info('Trying direct download with /public/ path', LogCategory.STORAGE, {
                    filePath: normalizedFilePath
                });
                // Get the file from Supabase storage with /public/ in the path
                const { data, error: storageError } = await supabase.storage
                    .from(`public/${StorageBucket.TEMPLATES}`)
                    .download(normalizedFilePath);
                if (storageError) {
                    throw storageError;
                }
                // Create a Blob from the file data
                const blob = new Blob([await data.arrayBuffer()], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                // Create a URL for the Blob
                const url = URL.createObjectURL(blob);
                // Create a download link and trigger the download
                const link = document.createElement('a');
                const fileName = normalizedFilePath.split('/').pop() || 'merged_document.xlsx';
                link.href = url;
                link.download = fileName;
                link.style.display = 'none';
                // Add to the document and click
                document.body.appendChild(link);
                link.click();
                // Clean up
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }, 100);
                logger.info('Document download triggered via direct download with /public/', LogCategory.GENERAL, { fileName });
                return true;
            }
            catch (directError) {
                logger.error('Error with direct download using /public/', LogCategory.STORAGE, {
                    error: directError,
                    filePath: normalizedFilePath
                });
                // Diagnostic information about the bucket and files
                try {
                    // Check if the bucket exists
                    const { data: bucketData, error: bucketError } = await supabase.storage
                        .getBucket(StorageBucket.TEMPLATES);
                    if (bucketError) {
                        logger.error('Error checking bucket existence', LogCategory.STORAGE, {
                            error: bucketError,
                            bucket: StorageBucket.TEMPLATES
                        });
                    }
                    else {
                        logger.info('Bucket exists', LogCategory.STORAGE, {
                            bucket: StorageBucket.TEMPLATES,
                            bucketDetails: bucketData
                        });
                        // List files in the bucket to check if the file exists
                        const { data: listData, error: listError } = await supabase.storage
                            .from(StorageBucket.TEMPLATES)
                            .list();
                        if (listError) {
                            logger.error('Error listing files in bucket', LogCategory.STORAGE, {
                                error: listError,
                                bucket: StorageBucket.TEMPLATES
                            });
                        }
                        else {
                            logger.info('Files in bucket', LogCategory.STORAGE, {
                                bucket: StorageBucket.TEMPLATES,
                                fileCount: listData?.length || 0,
                                files: listData?.map(f => f.name) || []
                            });
                            // Check if the file exists in the bucket
                            const fileExists = listData?.some(f => f.name === normalizedFilePath);
                            logger.info('File existence check', LogCategory.STORAGE, {
                                filePath: normalizedFilePath,
                                exists: fileExists
                            });
                        }
                    }
                }
                catch (bucketCheckError) {
                    logger.error('Exception checking bucket', LogCategory.STORAGE, {
                        error: bucketCheckError
                    });
                }
                throw directError;
            }
        }
        catch (error) {
            logger.error('Error downloading merged document', LogCategory.GENERAL, {
                error,
                errorMessage: error instanceof Error ? error.message : String(error),
                errorStack: error instanceof Error ? error.stack : undefined
            });
            return false;
        }
    }
    /**
     * Deletes a merged document by its file path
     * @param filePath The path of the file in the Templates bucket
     * @returns Promise resolving to true if successful
     */
    async deleteMergedDocument(filePath) {
        try {
            logger.info('Deleting merged document', LogCategory.GENERAL, { filePath });
            // Zuerst die Datei-ID aus der Datenbank ermitteln
            const { data: fileRecord, error: queryError } = await supabase
                .from('files')
                .select('id')
                .eq('name', filePath)
                .single();
            if (queryError) {
                logger.error('Error finding file record', LogCategory.GENERAL, { error: queryError, filePath });
                return false;
            }
            if (!fileRecord) {
                logger.error('File record not found', LogCategory.GENERAL, { filePath });
                return false;
            }
            // Dann alle Dokumente löschen, die auf diese Datei verweisen
            const { error: deleteDocError } = await supabase
                .from('documents')
                .delete()
                .eq('file_id', fileRecord.id);
            if (deleteDocError) {
                logger.error('Error deleting document records', LogCategory.GENERAL, { error: deleteDocError, fileId: fileRecord.id });
                return false;
            }
            logger.info('Successfully deleted document records', LogCategory.GENERAL, { fileId: fileRecord.id });
            // Jetzt die Datei selbst löschen
            const { deleteFile } = useStorage();
            const success = await deleteFile(StorageBucket.TEMPLATES, filePath);
            if (success) {
                logger.info('Successfully deleted merged document', LogCategory.GENERAL, { filePath });
                return true;
            }
            else {
                logger.error('Failed to delete merged document', LogCategory.GENERAL, { filePath });
                return false;
            }
        }
        catch (error) {
            logger.error('Error deleting merged document', LogCategory.GENERAL, { error, filePath });
            return false;
        }
    }
    /**
     * Gets all merged documents from the Templates bucket
     * @returns Promise resolving to an array of merged documents
     */
    async getMergedDocuments() {
        try {
            logger.info('Getting merged documents from Templates bucket', LogCategory.STORAGE);
            const { listFiles } = useStorage();
            // Get all files from the Templates bucket
            const files = await listFiles(StorageBucket.TEMPLATES);
            // Filter for merged documents (files that start with "merged_template_revexp_")
            const mergedFiles = files.filter(file => file.name && file.name.startsWith('merged_template_revexp_'));
            logger.info('Found merged documents in Templates bucket', LogCategory.STORAGE, {
                count: mergedFiles.length,
                fileNames: mergedFiles.map(f => f.name)
            });
            // Convert to document objects and sort by timestamp (newest first)
            return mergedFiles.map(file => {
                // Extract timestamp from filename (merged_template_revexp_1621234567890.xlsx)
                const timestamp = parseInt(file.name.split('_').pop()?.split('.')[0] || '0');
                return {
                    id: file.id || ('merged-' + timestamp),
                    name: file.name,
                    description: 'Merged template_revexp (uploaded to Templates bucket)',
                    status: DOCUMENT_STATUS.READY_FOR_DOWNLOAD,
                    report_type: REPORT_TYPE.PCO,
                    uploaded: true,
                    timestamp: timestamp
                };
            }).sort((a, b) => b.timestamp - a.timestamp); // Sort by timestamp (newest first)
        }
        catch (error) {
            logger.error('Error getting merged documents', LogCategory.STORAGE, { error });
            return [];
        }
    }
}
